import { useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAutoLogout } from '@/hooks/useAutoLogout';
import { useSessionManager } from '@/hooks/useSessionManager';

/**
 * AutoLogoutHandler component that manages automatic logout functionality
 * This component should be placed at the root level of the application
 * to ensure it's always active when a user is logged in.
 */
export default function AutoLogoutHandler() {
  const { user, logout } = useAuth();

  // Initialize auto logout functionality
  useAutoLogout({
    inactivityTimeout: 30 * 60 * 1000, // 30 minutes
    warningTime: 5 * 60 * 1000, // 5 minutes warning
    showWarning: true,
    warningMessage: 'You will be logged out due to inactivity in 5 minutes. Click anywhere to stay logged in.',
    user,
    logout
  });

  // Initialize session management
  useSessionManager({
    checkInterval: 5 * 60 * 1000, // Check every 5 minutes
    validateWithServer: true,
    sessionTimeout: 30 * 60 * 1000, // 30 minutes
    user,
    logout
  });

  useEffect(() => {
    if (!user) return;

    // Handle browser close, tab close, and page unload
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Clear any stored session data
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      
      // For better user experience, we can show a confirmation dialog
      // but this might be blocked by modern browsers
      const message = 'Are you sure you want to leave? You will be logged out.';
      event.returnValue = message;
      return message;
    };

    // Handle page visibility change (tab switching, minimizing)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // When tab becomes hidden, we can set a flag or perform cleanup
        // The useAutoLogout hook will handle the actual timeout logic
        console.log('Tab became hidden - auto logout timer active');
      } else {
        // When tab becomes visible again, reset any timers
        console.log('Tab became visible - resetting auto logout timer');
      }
    };

    // Handle page hide (mobile browsers, some desktop scenarios)
    const handlePageHide = () => {
      // Perform immediate cleanup when page is hidden
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
    };

    // Handle online/offline status
    const handleOnlineStatus = () => {
      if (!navigator.onLine) {
        // If user goes offline, we might want to log them out
        // or at least warn them about potential session loss
        console.log('User went offline - session may be affected');
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [user, logout]);

  // This component doesn't render anything
  return null;
}
